import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Sparkles, Users, Wallet, Settings2, MessageCircle,
  ArrowRight, ArrowLeft, Heart, CheckCircle2, Crown, Lightbulb, Gift,
} from 'lucide-react';

/**
 * 4-step Welcome Tour — MANDATORY slideshow for Beta members.
 * User CANNOT close it mid-way; they must click Back/Next through all 4 slides.
 * On the final slide, "Finish Tour" marks the tour as dismissed server-side.
 * Shown ONCE per user — after the first dismissal, the Quick Start checklist
 * takes over and the tour does not re-open.
 *
 * Visual system (iter152): premium dark glassmorphism + warm amber/gold accents,
 * two-column layout (left rail step navigator + main panel with 3D hero graphics).
 */
export default function WelcomeBetaPopup({ _preview = null } = {}) {
  const { api, user } = useAuth();
  const location = useLocation();
  const [data, setData] = useState(_preview?.data || null);
  const [open, setOpen] = useState(Boolean(_preview?.open));
  const [slide, setSlide] = useState(_preview?.slide || 0);
  const [finishing, setFinishing] = useState(false);
  // Session-level guard: once the popup has been shown (and dismissed) in this
  // browser tab, never re-open it on client-side navigation. Backend debounce
  // (12h) handles cross-session suppression.
  const shownThisSessionRef = useRef(false);

  const skipOnPath = /^\/(login|signup|set-password|beta-onboarding|trial-onboarding|free-trial|billing-setup)/i.test(location.pathname || '');

  useEffect(() => {
    let cancelled = false;
    if (_preview) return;       // preview mode: skip live fetch, use injected data
    if (!user) return;
    const run = async () => {
      try {
        const res = await api.get('/api/me/beta-status');
        if (cancelled) return;
        setData(res.data);
        if (res.data?.show_welcome_popup && !shownThisSessionRef.current) {
          shownThisSessionRef.current = true;
          setSlide(0);
          setOpen(true);
        }
      } catch { /* ignore */ }
    };
    run();
    // Re-check every 20 minutes in case backend state changes (stage advanced, etc.)
    const iv = setInterval(run, 20 * 60 * 1000);
    return () => { cancelled = true; clearInterval(iv); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id]);

  const finishTour = async () => {
    setFinishing(true);
    try {
      await api.post('/api/me/welcome-popup/dismiss', {
        stage: data?.welcome_popup_stage || null,
      });
    } catch { /* ignore */ }
    setFinishing(false);
    setOpen(false);
  };

  const openFeedback = () => {
    if (typeof window !== 'undefined') window.dispatchEvent(new Event('open-feedback-modal'));
  };

  if (data?.is_lifetime) return null;
  if (!data?.is_beta && !data?.welcome_bonus_granted) return null;
  if (skipOnPath) return null;
  const firstName = data?.first_name || user?.name?.split(' ')[0] || 'friend';

  const bonusCallout = {
    label: 'A welcome gift — $10 on us',
    text: "We've already loaded $10 of free credit into your workspace balance so you can test voice calls, chats and AI agents right away — no top-up needed. It's our way of saying welcome.",
    tone: 'amber',
    iconType: 'gift',
  };

  const rebillSlide = {
    icon: Settings2,
    navLabel: 'Re-billing',
    tag: 'Recommended Setup',
    hero: '/onboarding/step3-configure.png',
    title: 'Configure sub-account re-billing',
    subtitle: 'Since your first sub-account is your own, set it to Unlimited.',
    body:
      "That way, usage pulls straight from your agency balance with no cap — so your own calls and chats aren't interrupted while you're testing. Open Billing → find your sub-account → Set Limit → Unlimited.",
    callout: {
      label: 'Pro tip',
      text: 'Turn on Auto Top-off in Billing so your balance refills itself before running low.',
    },
  };

  const feedbackSlide = {
    icon: MessageCircle,
    navLabel: 'Feedback',
    tag: "We're Listening",
    hero: '/onboarding/step4-feedback.png',
    title: 'Your feedback matters — a lot',
    subtitle: "See that 'Share feedback' button at the top? Hit it. Often.",
    body:
      "Genuinely — we read every single message. Tell us what's broken, what's missing, what's confusing, or what would make you love this 10× more. That's how we'll shape Voxinity into the platform you actually want.",
    callout: {
      label: 'Try it now',
      text: 'Click the teal Share feedback button in the top header — drop us a note, attach a screenshot if it helps. You can send as many as you like.',
      action: { label: 'Open feedback', onClick: openFeedback },
    },
  };

  const slides = data?.is_beta ? [
    {
      icon: Users,
      navLabel: 'Welcome',
      tag: 'Getting Started',
      hero: '/onboarding/step1-welcome.png',
      title: `Welcome to the Voxinity AI Beta, ${firstName}`,
      subtitle: "You're one of the very first to experience what we're building.",
      body:
        "Your feedback during this period will directly shape what we build next — thank you for being part of this journey. We mean that. First step: create a sub-account — that's where your agents, phone numbers, and knowledge bases live.",
      callout: data?.welcome_bonus_granted ? bonusCallout : {
        label: 'What to do first',
        text: "Create a sub-account. Sub-accounts are where your agents, phone numbers, and knowledge bases live. You'll find Sub-Accounts in the sidebar.",
      },
    },
    {
      icon: Wallet,
      navLabel: 'Fund Workspace',
      tag: 'Beta Members Only',
      hero: '/onboarding/step2-fund.png',
      title: 'Fund your workspace',
      subtitle: 'Voxinity is pay-as-you-go — load balance and your agents start running.',
      body:
        'No monthly fees during beta. Usage is pulled in real time as your agents take calls and chats. Head to Billing in the sidebar and click Top Up Balance.',
      callout: {
        label: 'Beta Members only',
        text: `Lock in LIFETIME for ${data?.lifetime_price_label || '$3,000'} — one payment, never monthly. Price jumps to $5,000 once beta ends.`,
        tone: 'amber',
      },
    },
    rebillSlide,
    feedbackSlide,
  ] : [
    {
      icon: Users,
      navLabel: 'Welcome',
      tag: 'Getting Started',
      hero: '/onboarding/step1-welcome.png',
      title: `Welcome to Voxinity AI, ${firstName}`,
      subtitle: "We're so glad you're here — your workspace is ready to go.",
      body:
        "First step: create a sub-account. Sub-accounts are where your agents, phone numbers, and knowledge bases live — you'll find Sub-Accounts in the sidebar. From there, spin up your first AI agent in minutes.",
      callout: bonusCallout,
    },
    {
      icon: Wallet,
      navLabel: 'Your Balance',
      tag: 'Pay As You Go',
      hero: '/onboarding/step2-fund.png',
      title: 'Your balance & billing',
      subtitle: 'Voxinity is pay-as-you-go — usage pulls from your workspace balance in real time.',
      body:
        "Your $10 welcome credit is already in your balance — you'll see it in your transaction history as \"Welcome Bonus\". When you're ready for more, head to Billing in the sidebar and click Top Up Balance.",
      callout: {
        label: 'Pro tip',
        text: 'Turn on Auto Top-off in Billing so your balance refills itself before running low.',
      },
    },
    {
      ...rebillSlide,
      callout: {
        label: 'Where to find it',
        text: 'Billing → find your sub-account → Set Limit → Unlimited.',
      },
    },
    feedbackSlide,
  ];

  const current = slides[slide];
  const isLast = slide === slides.length - 1;
  const isFirst = slide === 0;

  const goldHeadline =
    'text-3xl sm:text-4xl lg:text-[2.6rem] font-bold tracking-tight leading-[1.1] bg-gradient-to-br from-[#FFE9A8] via-[#FFC34D] to-[#F59E0B] bg-clip-text text-transparent';

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 bg-black/70 backdrop-blur-xl"
          data-testid="welcome-beta-popup"
          data-slide={slide}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-5xl md:h-[710px] flex flex-col md:flex-row bg-black rounded-[28px] border border-white/10 shadow-[0_40px_120px_-20px_rgba(0,0,0,0.9),0_0_80px_rgba(245,158,11,0.06)] overflow-visible"
            data-testid="welcome-modal"
          >
            {/* Overlapping top-center pill badge */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-[#13131a] border border-amber-500/30 text-amber-300 px-5 py-1.5 rounded-full text-[10px] font-bold tracking-[0.22em] uppercase shadow-[0_0_18px_rgba(245,158,11,0.25)] z-20 whitespace-nowrap flex items-center gap-1.5">
              <Sparkles size={11} className="text-amber-400" />
              {current.tag}
            </div>

            {/* ───────── LEFT RAIL — step navigator ───────── */}
            <div className="w-full md:w-[18rem] flex flex-col gap-1.5 p-5 md:p-7 border-b md:border-b-0 md:border-r border-white/[0.06] shrink-0 bg-gradient-to-b from-white/[0.015] to-transparent rounded-t-[28px] md:rounded-t-none md:rounded-l-[28px]">
              <div className="hidden md:flex items-center gap-2 mb-4 px-1">
                {data?.is_beta && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[9px] font-extrabold tracking-[0.22em]"
                    style={{ background: 'linear-gradient(135deg,#FFE9A8,#F59E0B)', color: '#0a0b0f' }}>
                    <Crown size={9} /> BETA
                  </span>
                )}
                <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">Your setup journey</p>
              </div>

              <div className="flex md:flex-col gap-2 overflow-x-auto md:overflow-visible -mx-1 px-1 md:mx-0 md:px-0">
                {slides.map((s, i) => {
                  const NavIcon = s.icon;
                  const isActive = i === slide;
                  const isDone = i < slide;
                  return (
                    <button
                      key={i}
                      onClick={() => i < slide && setSlide(i)}
                      disabled={i > slide}
                      data-testid={`welcome-beta-step-nav-${i}`}
                      className={`flex items-center gap-3 p-2.5 rounded-xl transition-all duration-300 min-w-[180px] md:min-w-0 text-left ${
                        isActive
                          ? 'bg-white/[0.06] border border-amber-500/40 shadow-[0_0_18px_rgba(245,158,11,0.12)]'
                          : isDone
                            ? 'border border-transparent hover:bg-white/[0.04] cursor-pointer'
                            : 'border border-transparent opacity-45 cursor-default'
                      }`}
                    >
                      <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${
                        isActive
                          ? 'bg-gradient-to-br from-[#FFD98A] to-[#F59E0B] text-[#0a0b0f]'
                          : isDone
                            ? 'bg-amber-500/15 text-amber-300'
                            : 'bg-white/[0.04] text-zinc-500'
                      }`}>
                        {isDone ? <CheckCircle2 size={18} /> : <NavIcon size={17} />}
                      </div>
                      <div className="min-w-0">
                        <div className={`text-[9px] uppercase tracking-[0.14em] font-semibold ${isActive ? 'text-amber-300/80' : 'text-zinc-600'}`}>
                          Step {i + 1}
                        </div>
                        <div className={`text-[13px] font-medium truncate ${isActive ? 'text-white' : isDone ? 'text-zinc-300' : 'text-zinc-500'}`}>
                          {s.navLabel}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Signature — desktop only, anchored to bottom of rail */}
              <div className="hidden md:flex items-center gap-2 text-[11px] text-zinc-600 mt-auto pt-5 px-1">
                <Heart size={12} className="text-rose-400/80 flex-shrink-0" />
                <span>— Esteban &amp; the Voxinity Team</span>
              </div>
            </div>

            {/* ───────── MAIN PANEL ───────── */}
            <div className="relative flex-1 min-h-[560px] md:min-h-0 md:h-full overflow-hidden rounded-b-[28px] md:rounded-b-none md:rounded-r-[28px]">
              {/* Hero graphic — ABSOLUTELY PINNED, identical position/size on every step. The image
                  carries its own warm glow plume on pure black, so it blends into the black modal seamlessly. */}
              <div className="absolute inset-x-0 bottom-[92px] h-[200px] sm:h-[240px] z-0 pointer-events-none overflow-hidden">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={current.hero}
                    src={`${current.hero}?v=5`}
                    alt=""
                    aria-hidden
                    initial={{ opacity: 0, scale: 1.04 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.6, ease: 'easeOut' }}
                    className="absolute inset-0 w-full h-full object-cover object-bottom select-none"
                  />
                </AnimatePresence>
                {/* soft top-fade so the glow stays a low bloom and never beams up through the text */}
                <div aria-hidden className="absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-black via-black/85 to-transparent pointer-events-none" />
                {/* tiny bottom-fade to hide any faint reflection at the very bottom edge */}
                <div aria-hidden className="absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-black to-transparent pointer-events-none" />
              </div>

              {/* Content (top) */}
              <div className="relative z-10 p-6 md:p-9 lg:p-10">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={slide}
                    initial={{ opacity: 0, x: 24 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -24 }}
                    transition={{ duration: 0.3, ease: 'easeOut' }}
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-semibold" data-testid="welcome-beta-step-label">
                        Step {slide + 1} of {slides.length}
                      </span>
                    </div>

                    <h2 className={goldHeadline} data-testid="welcome-beta-popup-title">
                      {current.title}
                    </h2>
                    <p className="text-base sm:text-lg text-zinc-400 mt-2 leading-relaxed max-w-xl">
                      {current.subtitle}
                    </p>

                    <div className="relative rounded-2xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-sm p-4 mt-4 max-w-xl">
                      <p className="text-zinc-300/90 text-[14px] sm:text-[15px] leading-[1.65]">{current.body}</p>
                    </div>

                    {current.callout && (
                      <div className={`relative rounded-2xl border p-4 mt-3 max-w-xl ${
                        current.callout.tone === 'amber'
                          ? 'border-amber-400/30 bg-gradient-to-r from-amber-400/[0.10] to-amber-400/[0.03]'
                          : 'border-white/[0.08] bg-white/[0.025]'
                      }`}>
                        <div className="flex items-start gap-3">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${
                            current.callout.tone === 'amber' ? 'bg-amber-400/15' : 'bg-amber-400/10'
                          }`}>
                            {current.callout.iconType === 'gift'
                              ? <Gift className="text-amber-300" size={16} />
                              : current.callout.tone === 'amber'
                                ? <Crown className="text-amber-300" size={16} />
                                : <Lightbulb className="text-amber-300/90" size={16} />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold mb-1 text-amber-300/80">
                              {current.callout.label}
                            </div>
                            <p className="text-zinc-100 text-[14px] sm:text-[15px] leading-relaxed">{current.callout.text}</p>
                            {current.callout.action && (
                              <button
                                onClick={current.callout.action.onClick}
                                className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-400/15 text-amber-200 hover:bg-amber-400/25 text-[12px] font-medium border border-amber-400/30 transition-colors"
                                data-testid="welcome-beta-callout-action"
                              >
                                <MessageCircle size={12} />
                                {current.callout.action.label}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Navigation bar — ABSOLUTELY PINNED to the bottom, constant on every step */}
              <div className="absolute z-20 left-6 right-6 md:left-9 md:right-9 lg:left-10 lg:right-10 bottom-6 md:bottom-7 flex items-center justify-between gap-3 pt-5 border-t border-white/[0.06] bg-gradient-to-t from-black via-black to-transparent">
                <button
                  onClick={() => !isFirst && setSlide(slide - 1)}
                  disabled={isFirst}
                  className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full text-zinc-400 hover:text-white text-sm font-medium disabled:opacity-25 disabled:cursor-not-allowed transition-colors"
                  data-testid="welcome-beta-back-btn"
                >
                  <ArrowLeft size={14} /> Back
                </button>

                <div className="flex md:hidden items-center gap-1.5 text-[11px] text-zinc-600">
                  <Heart size={11} className="text-rose-400/80" />
                  <span>Voxinity Team</span>
                </div>

                {isLast ? (
                  <button
                    onClick={finishTour}
                    disabled={finishing}
                    className="inline-flex items-center gap-2 px-7 py-3 rounded-full font-semibold bg-white text-black hover:bg-zinc-200 shadow-[0_0_24px_rgba(255,255,255,0.18)] transition-all hover:translate-y-[-1px] disabled:opacity-50"
                    data-testid="welcome-beta-finish-btn"
                  >
                    {finishing ? 'Finishing…' : "Let's go"}
                    <ArrowRight size={15} />
                  </button>
                ) : (
                  <button
                    onClick={() => setSlide(slide + 1)}
                    className="inline-flex items-center gap-2 px-7 py-3 rounded-full font-semibold bg-white text-black hover:bg-zinc-200 shadow-[0_0_24px_rgba(255,255,255,0.18)] transition-all hover:translate-y-[-1px]"
                    data-testid="welcome-beta-next-btn"
                  >
                    Next
                    <ArrowRight size={15} />
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
